import { objectSpread2 as _objectSpread2 } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import isArrayLikeObject from 'lodash/isArrayLikeObject.js';
import isDate from 'lodash/isDate.js';
import { isObject, mixinOptionalProps } from './helpers.js';
import Locale from './locale.js';

const millisecondsPerDay = 24 * 60 * 60 * 1000;
function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}
class DateInfo {
  constructor(config) {
    let {
      locale = new Locale()
    } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    this.isDateInfo = true;
    this.isRange = isObject(config) || typeof config === 'function';
    this.isDate = !this.isRange;
    this.locale = locale;
    this.mask = locale.masks.data;
    this.getMonthComps = locale.getMonthComps;
    this.firstDayOfWeek = locale.firstDayOfWeek;
    if (this.isDate) {
      this.type = 'date';
      let date = this.toDate(config);
      date = isDate(date) ? date : new Date();
      date.setHours(0, 0, 0, 0);
      this.date = date;
      this.dateTime = date.getTime();
    }
    if (this.isRange) {
      this.type = 'range';
      if (typeof config === 'function') {
        this.on = {
          and: config
        };
      } else {
        let start = this.toDate(config.start);
        let end = this.toDate(config.end);
        if (start && end && start > end) {
          const temp = start;
          start = end;
          end = temp;
        } else if (start && config.span >= 1) {
          end = addDays(start, config.span - 1);
        }
        if (start) {
          if (!isDate(start)) {
            start = null;
          } else {
            start.setHours(0, 0, 0, 0);
          }
        }
        if (end) {
          if (!isDate(end)) {
            end = null;
          } else {
            end.setHours(0, 0, 0, 0);
          }
        }
        this.start = start;
        this.end = end;
        this.startTime = start && start.getTime();
        this.endTime = end && end.getTime();
        if (start && end) {
          this.daySpan = this.diffInDays(start, end);
          this.weekSpan = this.diffInWeeks(start, end);
          this.monthSpan = this.diffInMonths(start, end);
          this.yearSpan = this.diffInYears(start, end);
        }
        const andOpt = mixinOptionalProps(config, {}, DateInfo.patternProps);
        if (andOpt.assigned) {
          this.on = {
            and: andOpt.target
          };
        }
        if (config.on) {
          const or = (isArrayLikeObject(config.on) ? config.on : [config.on]).map(op => {
            if (typeof op === 'function') {
              return op;
            }
            const opt = mixinOptionalProps(op, {}, DateInfo.patternProps);
            return opt.assigned ? opt.target : null;
          }).filter(op => op);
          if (or.length) {
            this.on = _objectSpread2(_objectSpread2({}, this.on), {}, {
              or
            });
          }
        }
      }
      this.isComplex = !!this.on;
    }
  }
  toDate(date) {
    const mask = this.locale.masks.data;
    return this.locale.toDate(date, mask);
  }
  findShallowIntersectingRange(date1, date2) {
    const thisRange = date1.toRange();
    const otherRange = date2.toRange();
    let start = null;
    let end = null;
    if (thisRange.start) {
      if (!otherRange.start) {
        start = thisRange.start;
      } else {
        start = thisRange.start < otherRange.start ? thisRange.start : otherRange.start;
      }
    } else if (otherRange.start) {
      start = otherRange.start;
    }
    if (thisRange.end && (!start || thisRange.end >= start)) {
      end = thisRange.end;
    }
    if (otherRange.end && (!start || otherRange.end >= start)) {
      if (!end || otherRange.end < end) {
        end = otherRange.end;
      }
    }
    return {
      start,
      end
    };
  }
  startOfWeek(date) {
    const day = date.getDay() + 1;
    const daysToAdd = day >= this.firstDayOfWeek ? this.firstDayOfWeek - day : -(7 - (this.firstDayOfWeek - day));
    return addDays(date, daysToAdd);
  }
  diffInDays(d1, d2) {
    return Math.round((d2 - d1) / millisecondsPerDay);
  }
  diffInWeeks(d1, d2) {
    return this.diffInDays(this.startOfWeek(d1), this.startOfWeek(d2));
  }
  diffInYears(d1, d2) {
    return d2.getUTCFullYear() - d1.getUTCFullYear();
  }
  diffInMonths(d1, d2) {
    return this.diffInYears(d1, d2) * 12 + (d2.getMonth() - d1.getMonth());
  }
  static get patterns() {
    return {
      dailyInterval: {
        test: (day, interval, di) => di.diffInDays(di.start || new Date(), day.date) % interval === 0
      },
      weeklyInterval: {
        test: (day, interval, di) => di.diffInWeeks(di.start || new Date(), day.date) % interval === 0
      },
      monthlyInterval: {
        test: (day, interval, di) => di.diffInMonths(di.start || new Date(), day.date) % interval === 0
      },
      yearlyInterval: {
        test: () => (day, interval, di) => di.diffInYears(di.start || new Date(), day.date) % interval === 0
      },
      days: {
        validate: days => isArrayLikeObject(days) ? days : [parseInt(days, 10)],
        test: (day, days) => days.includes(day.day) || days.includes(-day.dayFromEnd)
      },
      weekdays: {
        validate: weekdays => isArrayLikeObject(weekdays) ? weekdays : [parseInt(weekdays, 10)],
        test: (day, weekdays) => weekdays.includes(day.weekday)
      },
      ordinalWeekdays: {
        validate: ordinalWeekdays => Object.keys(ordinalWeekdays).reduce((obj, ck) => {
          const weekdays = ordinalWeekdays[ck];
          if (!weekdays) {
            return obj;
          }
          obj[ck] = isArrayLikeObject(weekdays) ? weekdays : [parseInt(weekdays, 10)];
          return obj;
        }, {}),
        test: (day, ordinalWeekdays) => Object.keys(ordinalWeekdays).map(key => parseInt(key, 10)).find(key => ordinalWeekdays[key].includes(day.weekday) && (key === day.weekdayOrdinal || key === -day.weekdayOrdinalFromEnd))
      },
      weekends: {
        validate: config => config,
        test: day => day.weekday === 1 || day.weekday === 7
      },
      workweek: {
        validate: config => config,
        test: day => day.weekday >= 2 && day.weekday <= 6
      },
      weeks: {
        validate: weeks => isArrayLikeObject(weeks) ? weeks : [parseInt(weeks, 10)],
        test: (day, weeks) => weeks.includes(day.week) || weeks.includes(-day.weekFromEnd)
      },
      months: {
        validate: months => isArrayLikeObject(months) ? months : [parseInt(months, 10)],
        test: (day, months) => months.includes(day.month)
      },
      years: {
        validate: years => isArrayLikeObject(years) ? years : [parseInt(years, 10)],
        test: (day, years) => years.includes(day.year)
      }
    };
  }
  static get patternProps() {
    return Object.keys(DateInfo.patterns).map(key => ({
      name: key,
      validate: DateInfo.patterns[key].validate
    }));
  }
  static testConfig(config, day, dateInfo) {
    if (typeof config === 'function') {
      return config(day);
    }
    if (isObject(config)) {
      return Object.keys(config).every(key => DateInfo.patterns[key].test(day, config[key], dateInfo));
    }
    return null;
  }
  iterateDatesInRange(_ref, func) {
    let {
      start,
      end
    } = _ref;
    if (!start || !end || typeof func !== 'function') {
      return null;
    }
    const state = {
      i: 0,
      date: start,
      day: this.locale.getDayFromDate(start),
      finished: false
    };
    let result = null;
    for (; !state.finished && state.date <= end; state.i++) {
      result = func(state);
      state.date = addDays(state.date, 1);
      state.day = this.locale.getDayFromDate(state.date);
    }
    return result;
  }
  intersectsDate(other) {
    const date = other.isDateInfo ? other : new DateInfo(other, {
      locale: this.locale
    });
    if (!this.shallowIntersectsDate(date)) {
      return null;
    }
    if (!this.on) {
      return this;
    }
    const range = this.findShallowIntersectingRange(this, date);
    let result = false;
    this.iterateDatesInRange(range, state => {
      if (this.matchesDay(state.day)) {
        result = result || date.matchesDay(state.day);
        state.finished = result;
      }
    });
    return result;
  }
  shallowIntersectsDate(other) {
    return this.dateShallowIntersectsDate(this, other.isDate ? other : new DateInfo(other, {
      locale: this.locale
    }));
  }
  dateShallowIntersectsDate(date1, date2) {
    if (date1.isDate) {
      return date2.isDate ? date1.dateTime === date2.dateTime : this.dateShallowIncludesDate(date2, date1);
    }
    if (date2.isDate) {
      return this.dateShallowIncludesDate(date1, date2);
    }
    if (date1.start && date2.end && date1.start > date2.end) {
      return false;
    }
    if (date1.end && date2.start && date1.end < date2.start) {
      return false;
    }
    return true;
  }
  includesDate(other) {
    const date = other.isDateInfo ? other : new DateInfo(other, {
      locale: this.locale
    });
    if (!this.shallowIncludesDate(date)) {
      return false;
    }
    if (!this.on) {
      return true;
    }
    const range = this.findShallowIntersectingRange(this, date);
    let result = true;
    this.iterateDatesInRange(range, state => {
      if (this.matchesDay(state.day)) {
        result = result && date.matchesDay(state.day);
        state.finished = !result;
      }
    });
    return result;
  }
  shallowIncludesDate(other) {
    return this.dateShallowIncludesDate(this, other.isDate ? other : new DateInfo(other, {
      locale: this.locale
    }));
  }
  dateShallowIncludesDate(date1, date2) {
    if (date1.isDate) {
      if (date2.isDate) {
        return date1.dateTime === date2.dateTime;
      }
      if (!date2.startTime || !date2.endTime) {
        return false;
      }
      return date1.dateTime === date2.startTime && date1.dateTime === date2.endTime;
    }
    if (date2.isDate) {
      if (date1.start && date2.date < date1.start) {
        return false;
      }
      if (date1.end && date2.date > date1.end) {
        return false;
      }
      return true;
    }
    if (date1.start && (!date2.start || date2.start < date1.start)) {
      return false;
    }
    if (date1.end && (!date2.end || date2.end > date1.end)) {
      return false;
    }
    return true;
  }
  includesDay(day) {
    if (!this.shallowIncludesDate(day.date)) {
      return null;
    }
    return this.matchesDay(day) ? this : null;
  }
  matchesDay(day) {
    if (!this.on) {
      return true;
    }
    if (this.on.and && !DateInfo.testConfig(this.on.and, day, this)) {
      return false;
    }
    if (this.on.or && !this.on.or.some(or => DateInfo.testConfig(or, day, this))) {
      return false;
    }
    return true;
  }
  toRange() {
    if (this.isDate) {
      return new DateInfo({
        start: this.date,
        end: this.date
      }, {
        locale: this.locale
      });
    }
    return new DateInfo({
      start: this.start,
      end: this.end
    }, {
      locale: this.locale
    });
  }
}

export { addDays, DateInfo as default };
//# sourceMappingURL=date-info.js.map
